import React, { useState, useEffect } from "react";
import Container from "@mui/material/Container";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import Login from "./Login";
import BookingCalendar from "./BookingCalendar";
import AdminDashboard from "./AdminDashboard";
import RankingDashboard from "./RankingDashboard";
import UserProfile from "./UserProfile";
import PasswordReset from "./PasswordReset";
import { authApi } from "./api";
import "./App.css";

const clubTheme = createTheme({
  palette: {
    primary: { main: "#124f7b", dark: "#0a3556", contrastText: "#ffffff" },
    secondary: { main: "#ec6f2d", contrastText: "#ffffff" },
    success: { main: "#39865b" },
    warning: { main: "#db8b16" },
    background: { default: "#f3f7f9", paper: "#ffffff" },
    text: { primary: "#132633", secondary: "#607783" },
  },
  typography: {
    fontFamily: "'Manrope', 'Segoe UI', sans-serif",
    h4: { fontWeight: 800, letterSpacing: "-0.035em" },
    h5: { fontWeight: 800, letterSpacing: "-0.025em" },
    h6: { fontWeight: 750 },
    button: { fontWeight: 750, textTransform: "none" },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 10, paddingInline: 18, boxShadow: "none" },
        contained: { boxShadow: "0 6px 16px rgba(18, 79, 123, 0.18)" },
      },
    },
    MuiTextField: { defaultProps: { variant: "outlined" } },
    MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 10 } } },
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderColor: "#cadbe3",
          color: "#124f7b",
          fontWeight: 750,
          textTransform: "none",
          "&.Mui-selected": { color: "#fff", backgroundColor: "#124f7b" },
          "&.Mui-selected:hover": { color: "#fff", backgroundColor: "#0a3556" },
        },
      },
    },
  },
});

function App() {
  const [userState, setUserState] = useState(null);
  const [page, setPage] = useState("main");
  const [resetToken, setResetToken] = useState(() => new URLSearchParams(window.location.search).get("reset"));
  const [loginNotice, setLoginNotice] = useState("");

  useEffect(() => {
    authApi.me()
      .then(data => setUserState(data.user || data))
      .catch(() => setUserState(null));
  }, []);

  const finishReset = (message) => {
    window.history.replaceState(null, "", window.location.pathname);
    setResetToken(null);
    setLoginNotice(message);
    if (message) setUserState(null);
  };

  const handleLogout = async () => {
    setUserState(null);
    await authApi.logout();
  };

  return (
    <ThemeProvider theme={clubTheme}>
      <Box className="app-shell">
        <Container maxWidth="lg">
          <AppBar
            className="club-header"
            position="static"
            elevation={0}
            sx={{
              borderRadius: { xs: 0, sm: 3 },
              background: "linear-gradient(120deg, #0a3556 0%, #124f7b 65%, #176592 100%)",
              boxShadow: "0 16px 40px rgba(10, 53, 86, 0.2)",
            }}
          >
            <Toolbar sx={{ minHeight: { xs: 88, sm: 110 }, px: { xs: 2, sm: 3.5 }, gap: { xs: 1.5, sm: 2.5 } }}>
              <img src="/logo-pergkirchen.png" alt="DSG Union Pergkirchen" className="club-logo" />
              <Box sx={{ zIndex: 1, minWidth: 0, flexGrow: 1 }}>
                <Typography sx={{ color: "#f3cf19", fontSize: { xs: 11, sm: 13 }, fontWeight: 800, letterSpacing: ".14em", textTransform: "uppercase", mb: 0.4 }}>
                  DSG Union Pergkirchen
                </Typography>
                <Typography variant="h4" sx={{ color: "#fff", fontSize: { xs: 21, sm: 32 }, lineHeight: 1.1 }}>
                  Tennisplatz-Buchung
                </Typography>
              </Box>
            </Toolbar>
          </AppBar>

          <Box className="content-panel" sx={{ mt: { xs: 2, sm: 3 } }}>
            {resetToken && <PasswordReset token={resetToken} onDone={finishReset} />}

            {!resetToken && !userState && <Login setUserState={setUserState} notice={loginNotice} />}

            {!resetToken && userState && !userState.approved && (
              <Box sx={{ maxWidth: 620, mx: "auto", p: { xs: 3, sm: 5 }, textAlign: "center", bgcolor: "background.paper", borderRadius: 3, boxShadow: "0 14px 40px rgba(17,54,75,.1)", borderTop: "5px solid #f3cf19" }}>
                <Typography variant="h5" color="warning.main">Account wartet auf Freigabe</Typography>
                <Typography color="text.secondary" sx={{ mt: 1 }}>Ein Admin muss deinen Zugang noch bestätigen.</Typography>
                <Button sx={{ mt: 3 }} variant="outlined" startIcon={<LogoutRoundedIcon />} onClick={handleLogout}>Abmelden</Button>
              </Box>
            )}

            {!resetToken && userState && userState.approved && (
              <Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.2, mb: 2.5, p: { xs: 1.5, sm: 2 }, bgcolor: "rgba(255,255,255,.78)", backdropFilter: "blur(10px)", border: "1px solid rgba(18,79,123,.1)", borderRadius: 2.5 }}>
                  <Box sx={{ mr: "auto", minWidth: 0 }}>
                    <Typography variant="h6" sx={{ lineHeight: 1.25 }}>Willkommen am Platz!</Typography>
                    <Typography color="text.secondary" noWrap sx={{ fontSize: 13 }}>{userState.email}</Typography>
                  </Box>
                  {userState.isAdmin && <Chip label="Administrator" size="small" sx={{ bgcolor: "rgba(243,207,25,.22)", color: "#765f00", fontWeight: 800 }} />}
                  <Button variant={page === "main" ? "contained" : "outlined"} startIcon={<CalendarMonthRoundedIcon />} onClick={() => setPage("main")}>Buchung</Button>
                  <Button variant={page === "ranking" ? "contained" : "outlined"} color={page === "ranking" ? "secondary" : "primary"} startIcon={<EmojiEventsRoundedIcon />} onClick={() => setPage("ranking")}>Rangliste</Button>
                  <Button variant={page === "profile" ? "contained" : "outlined"} startIcon={<PersonRoundedIcon />} onClick={() => setPage("profile")}>Mein Profil</Button>
                  <Button variant="text" startIcon={<LogoutRoundedIcon />} onClick={handleLogout}>Abmelden</Button>
                </Box>

                {!userState.active && !userState.isAdmin && (
                  <Alert severity="warning" sx={{ mb: 2.5 }}>
                    Dein Zugang ist derzeit inaktiv. Du kannst den Kalender und die Rangliste ansehen, Buchungen sind aber erst nach Einzahlung des Mitgliedsbeitrags und Freischaltung durch einen Admin wieder möglich.
                  </Alert>
                )}

                <Box sx={{ p: { xs: 1.25, sm: 2.5 }, bgcolor: "background.paper", borderRadius: 3, boxShadow: "0 14px 40px rgba(17,54,75,.1)", border: "1px solid rgba(18,79,123,.08)" }}>
                  {page === "main" && <BookingCalendar user={userState} />}
                  {page === "ranking" && <RankingDashboard user={userState} />}
                  {page === "profile" && <UserProfile user={userState} />}
                </Box>

                {userState.isAdmin && page === "main" && (
                  <Box sx={{ mt: 5, p: { xs: 1.25, sm: 2.5 }, bgcolor: "background.paper", borderRadius: 3, boxShadow: "0 14px 40px rgba(17,54,75,.1)" }}>
                    <AdminDashboard />
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default App;
